import {
  ChangeDetectorRef,
  Directive,
  inject,
  Input,
  OnDestroy,
  OnInit,
  SimpleChange,
  ViewContainerRef,
} from '@angular/core';
import { TooltipDirective } from '@epicuro-next/ataraxia/tooltip';
import { AbstractDataProvider } from '@epicuro-next/data-providers/base-data-provider';
import { DataProvidersResolver } from '@epicuro-next/data-providers/providers-registry';
import {
  IDynamicBindableValue,
  IDynamicItemValue,
  isOperation,
  Operation,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { LogicOperationsService } from '@epicuro-next/platform/dynamic-logical-operations';
import { isLocaleMessageId } from '@epicuro-next/platform/dynamic-value-resolving';
import { DynamicLocalePipe } from '@epicuro-next/platform/i18n';
import { isNullish, isPrimitive, Placement } from '@epicuro-next/utilities';
import { BehaviorSubject, Subject, Subscription, takeUntil } from 'rxjs';

@Directive({
  selector: '[epcDynamicElementsTooltip]',
  providers: [DynamicLocalePipe],
  standalone: true,
  hostDirectives: [TooltipDirective],
})
export class DynamicElementsTooltipDirective implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private atxTooltip = inject(TooltipDirective);
  private dataProvidersResolver = inject(DataProvidersResolver);
  private localePipe = inject(DynamicLocalePipe);
  private lo = inject(LogicOperationsService);
  private vcRef = inject(ViewContainerRef);
  // tslint:disable-next-line:variable-name
  private _tooltip$ = new BehaviorSubject('');
  private disabledSubscription: Subscription | null = null;
  private service: AbstractDataProvider | null = null;
  private destroy$ = new Subject<void>();
  private subscription?: Subscription;

  @Input()
  public set tooltip(value: IDynamicItemValue) {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (isPrimitive(value) || isNullish(value)) {
      this._tooltip$.next(
        isLocaleMessageId(value)
          ? this.localePipe.transform(value as string)
          : (value as string),
      );
      return;
    }

    const { property, provider } = value as IDynamicBindableValue;

    if (!this.service) {
      this.service = this.dataProvidersResolver.resolve(provider);
    }

    if (!this.service) {
      return;
    }

    this.subscription = this.service
      .read<string>(property)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resolvedValue) => {
        this._tooltip$.next(resolvedValue);
      });
  }

  @Input() set tooltipPlacement(placement: Placement) {
    this.atxTooltip.tooltipPlacement = placement;
  }

  @Input() set tooltipStyling(tooltipStyling: string) {
    this.atxTooltip.tooltipStyling = tooltipStyling;
  }

  @Input() set tooltipEnabled(value: Operation | boolean) {
    if (isNullish(value)) return;

    if (typeof value === 'boolean') {
      this.atxTooltip.tooltipEnabled = value;
      return;
    }

    if (isOperation(value)) {
      if (this.disabledSubscription) {
        this.disabledSubscription.unsubscribe();
        this.disabledSubscription = null;
      }

      this.disabledSubscription = this.lo
        .performOperationCheck(value, this.vcRef.injector)
        .pipe(takeUntil(this.destroy$))
        .subscribe((operationResult) => {
          this.atxTooltip.tooltipEnabled = operationResult;

          if (!operationResult) {
            // needs to trigger detach method
            this.atxTooltip.ngOnChanges({
              tooltipEnabled: new SimpleChange(false, false, false),
            });
          }
          this.cdr.markForCheck();
        });
    }
  }

  public ngOnInit(): void {
    this._tooltip$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.atxTooltip.atxTooltip = value;
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
